import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useExpanded,
  useMountedLayoutEffect,
  usePagination,
  useRowSelect,
  useTable,
} from "react-table";
import "simplebar";
import "simplebar/dist/simplebar.css";
import { TranslatedText } from "../../../helpers/helpers";
import "./DataTable.scss";

// A simple way to support a renderRowSubComponent is to make a render prop
// This is NOT part of the React Table API, it's merely a rendering
// option we are creating for ourselves in our table renderer
function Table({
  columns: userColumns,
  data,
  renderRowSubComponent,
  getRowProps,
  selectedRows,
  downloaded = false,
  setDownloaded = () => {},
  onSelectedRowsChange,
  withBackendPagination,
  backendPaginationNextPage,
  backendPaginationPrevPage,
  backendPaginationCurrentPage,
  backendPaginationTotPages,
  singleCheckbox,
  setClickedRow,
}) {
  const { t } = useTranslation();
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    visibleColumns,
    selectedFlatRows,
    toggleAllRowsSelected,
    state: { selectedRowIds, pageIndex },
  } = useTable(
    {
      columns: userColumns,
      data,
      disableSortBy: true,
      initialState: {
        selectedRowIds: selectedRows,
        pageIndex: 0,
        pageSize: 10,
      },
      manualPagination: withBackendPagination ? true : false,
    },
    //useSortBy,
    useExpanded,// We can useExpanded to track the expanded state for sub components too!
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    if (singleCheckbox) {
      const holder = selectedFlatRows;
      toggleAllRowsSelected(false);
      setClickedRow(null);
      setTimeout(() => {
        holder.forEach((row) => {
          if (row.index == singleCheckbox) {
            row.toggleRowSelected(true);
          }
        });
      }, 20);
    }
  }, [singleCheckbox]);

  useEffect(() => {
    toggleAllRowsSelected(false);
    setDownloaded(false);
  }, [downloaded]);

  useMountedLayoutEffect(() => {
    const selectedRowData = selectedFlatRows.map((d) => d.original);
    if (singleCheckbox) {
      if (selectedRowData.length < 2) {
        onSelectedRowsChange &&
          onSelectedRowsChange(selectedRowIds, selectedRowData);
      }
    } else {
      onSelectedRowsChange &&
        onSelectedRowsChange(selectedRowIds, selectedRowData);
    }
  }, [onSelectedRowsChange, selectedRowIds]);

  const visible = data.length > 5 ? "" : "hidden";
  const visibleBackendPagination =
    data.length <= 5 && backendPaginationCurrentPage === 1 ? "hidden" : "";

  return (
    <>
      <div className="table-responsive scrollable-content" data-simplebar>
        <table {...getTableProps()} className="table table-striped">
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: headerRowKey, ...restHeaderGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerRowKey} {...restHeaderGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...restColumnProps } =
                      column.getHeaderProps(
                        column.getSortByToggleProps
                          ? column.getSortByToggleProps()
                          : {}
                      );

                    if (column.id === "contractId") {
                      const active = column.isSorted
                        ? column.isSortedDesc
                          ? "down"
                          : "up"
                        : "";
                      return (
                        <th
                          key={colKey}
                          className="d-flex"
                          scope="row"
                          {...restColumnProps}
                        >
                          {column.render("Header")}
                          <div className={"d-flex flex-column " + active}>
                            <i
                              className="gc-icon-functional-chevron-down rotate"
                              aria-hidden="true"
                            ></i>
                            <i
                              className="gc-icon-functional-chevron-down icon-down"
                              aria-hidden="true"
                            ></i>
                          </div>
                        </th>
                      );
                    }

                    if (column.id === "iconDownload") {
                      return (
                        <th
                          key={colKey}
                          scope="col"
                          {...restColumnProps}
                          aria-label={t("download.document.label")}
                        >
                          {column.render("Header")}
                        </th>
                      );
                    }

                    if (column.id === "checkBox") {
                      if (column.meta?.omitThAria) {
                        const { ["aria-label"]: _omit, ...restWithoutAria } =
                          restColumnProps;
                        return (
                          <th key={colKey} scope="col" {...restWithoutAria}>
                            {column.render("Header")}
                          </th>
                        );
                      }
                      return (
                        <th
                          key={colKey}
                          scope="col"
                          {...restColumnProps}
                          aria-label={t("header.checkbox.label")}
                        >
                          {column.render("Header")}
                        </th>
                      );
                    }

                    if (column.id === "expander") {
                      const hasLabel =
                        restColumnProps["aria-label"] || column.label;
                      if (column.meta?.omitThAria) {
                        const { ["aria-label"]: _omit, ...restWithoutAria } =
                          restColumnProps;
                        return (
                          <th key={colKey} scope="col" {...restWithoutAria}>
                            {column.render("Header")}
                          </th>
                        );
                      }
                      return (
                        <th
                          key={colKey}
                          scope="col"
                          {...restColumnProps}
                          {...(!hasLabel
                            ? { "aria-label": t("header.expander.label") }
                            : {})}
                        >
                          {column.render("Header")}
                        </th>
                      );
                    }

                    return (
                      <th key={colKey} scope="row" {...restColumnProps}>
                        {column.render("Header")}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const { key: rowKey, ...restRowProps } = row.getRowProps(
                getRowProps(row)
              );

              return (
                // Use a React.Fragment here so the table markup is still valid
                <React.Fragment key={rowKey}>
                  <tr {...restRowProps}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...restCellProps } =
                        cell.getCellProps();
                      return (
                        <td key={cellKey} {...restCellProps}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                  {row.isExpanded ? (
                    <tr
                      key={`${rowKey}-expanded`}
                      className="expandend-row-table"
                      id={`accordion-panel-${row.id}`}
                      role="region"
                      aria-labelledby={`expand-button-${row.id}`}
                    >
                      <td colSpan={visibleColumns.length}>
                        {/*
                          Inside it, call our renderRowSubComponent function. In reality,
                          you could pass whatever you want as props to
                          a component like this, including the entire
                          table instance. But for this example, we'll just
                          pass the row
                        */}
                        {renderRowSubComponent({ row })}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {withBackendPagination ? (
        <div className={"pagination " + visibleBackendPagination}>
          <button
            onClick={() => {
              if (backendPaginationCurrentPage != 1)
                backendPaginationPrevPage();
            }}
            aria-label={t("aria.page.link.prev")}
            aria-disabled={backendPaginationCurrentPage == 1}
            className={`btn btn-icon btn-icon-air ${
              backendPaginationCurrentPage == 1 ? "disabled" : ""
            }`}
          >
            <i className="gc-icon-functional-arrow-back" aria-hidden="true"></i>
          </button>
          <span className="pagiantion-info">
            <strong>
              <span aria-current={t("aria.page.current")}>
                {backendPaginationCurrentPage}
              </span>
              <span className="total-pages">
                {" "}
                <TranslatedText i18nKey="common.from" />{" "}
                {backendPaginationTotPages}
              </span>
            </strong>
          </span>
          <button
            onClick={() => {
              if (backendPaginationCurrentPage != backendPaginationTotPages)
                backendPaginationNextPage();
            }}
            aria-label={t("aria.page.link.next")}
            aria-disabled={
              backendPaginationCurrentPage == backendPaginationTotPages
            }
            className={`btn btn-icon btn-icon-air ${
              backendPaginationCurrentPage == backendPaginationTotPages
                ? "disabled"
                : ""
            }`}
          >
            <i className="gc-icon-functional-arrow-right" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <div className={"pagination " + visible}>
          <button
            onClick={() => {
              if (canPreviousPage) previousPage();
            }}
            aria-label={t("aria.page.link.prev")}
            aria-disabled={!canPreviousPage}
            className={`btn btn-icon btn-icon-air ${
              !canPreviousPage ? "disabled" : ""
            }`}
          >
            <i className="gc-icon-functional-arrow-back" aria-hidden="true"></i>
          </button>
          <span className="pagiantion-info">
            <strong>
              <span aria-current={t("aria.page.current")}>{pageIndex + 1}</span>
              <span className="total-pages">
                {" "}
                <TranslatedText i18nKey="common.from" /> {pageOptions.length}
              </span>
            </strong>
          </span>
          <button
            onClick={() => {
              if (canNextPage) nextPage();
            }}
            aria-label={t("aria.page.link.next")}
            aria-disabled={!canNextPage}
            className={`btn btn-icon btn-icon-air ${
              !canNextPage ? "disabled" : ""
            }`}
          >
            <i className="gc-icon-functional-arrow-right" aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  );
}

function DataTable(props) {
  const [selectedRows] = useState({});
  return (
    <div className="DataTable" data-testid="DataTable">
      <Table
        columns={props.columns}
        data={props.data}
        selectedRows={selectedRows}
        onSelectedRowsChange={props.selectedRowsIds}
        withBackendPagination={props.isBackendPaginationActive}
        backendPaginationNextPage={props.goNextPage}
        backendPaginationPrevPage={props.goPrevPage}
        backendPaginationCurrentPage={props.activePage}
        backendPaginationTotPages={props.totPages}
        singleCheckbox={props.singleCheckbox}
        setClickedRow={props.setClickedRow}
        downloaded={props.isDownloaded}
        setDownloaded={props.setDownloaded}
        // We added this as a prop for our table component
        // Remember, this is not part of the React Table API,
        // it's merely a rendering option we created for
        // ourselves
        renderRowSubComponent={props.renderRowSubComponent}
        getRowProps={(row) => ({ style: {} })}
      />
    </div>
  );
}

export default DataTable;
